import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * Renders the Neural Machines lockup or icon.
 * - `variant=lockup` → wide brand mark (auto-aspect 2789:971)
 * - `variant=icon`   → square mark
 * - `tone=auto` follows current dark/light theme
 * - `tone=light` always uses light/white asset (eg over dark backgrounds)
 */
type Tone = 'auto' | 'light' | 'dark';

export function Logo({
  variant = 'lockup',
  tone = 'auto',
  className,
}: {
  variant?: 'lockup' | 'icon';
  tone?: Tone;
  className?: string;
}) {
  const [isDark, setIsDark] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark'),
  );

  useEffect(() => {
    const obs = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark')),
    );
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const useLight = tone === 'light' || (tone === 'auto' && isDark);
  const src = variant === 'icon'
    ? (useLight ? '/brand/icon_white.svg' : '/brand/icon.svg')
    : (useLight ? '/brand/lockup_white.svg' : '/brand/lockup.svg');

  return (
    <img
      src={src}
      alt="Neural Machines"
      // The default Tailwind preflight already gives images max-width:100% height:auto.
      // We add explicit width:auto so a parent setting `h-9` doesn't force the SVG
      // to fill its intrinsic 2789-pixel width.
      style={{ width: 'auto', maxWidth: '100%' }}
      className={cn('block select-none object-contain', className)}
      draggable={false}
    />
  );
}
